import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { z } from 'zod';

// src/error-codes.ts
var SREQUIVZ_LIB = "@norkron/srequivz";
var SREQUIVZ_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.srequivz");
var { warn, panic, reportInvalidInput } = createScopedErrors(
  SREQUIVZ_LIB,
  SREQUIVZ_TOKEN
);
try {
  registerCodes(SREQUIVZ_LIB, {
    SREQUIVZ_INVALID_INPUT: {
      description: "A public input to @norkron/srequivz failed zod validation (Wave 10 schema mandate). Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
      severity: "warning",
      docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE"
    }
  });
} catch {
}

// src/accessible-name.ts
var NAMING_TAGS = /* @__PURE__ */ new Set([
  "button",
  "a",
  "input",
  "textarea",
  "select",
  "img",
  "area",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
]);
function isNamingTarget(el) {
  return NAMING_TAGS.has(el.tagName.toLowerCase()) || el.hasAttribute("role");
}
function resolveLabelledBy(doc, ids) {
  const parts = [];
  for (const id of ids.trim().split(/\s+/)) {
    const escaped = id.replace(/"/g, '\\"');
    const matches = doc.querySelectorAll(`[id="${escaped}"]`);
    for (let i = 0; i < matches.length; i++) {
      const target = matches[i];
      if (!target) continue;
      parts.push((target.textContent ?? "").trim());
    }
  }
  return parts.filter(Boolean).join(" ");
}
function resolveLabelFor(doc, controlId) {
  const escaped = controlId.replace(/"/g, '\\"');
  const labels = doc.querySelectorAll(`label[for="${escaped}"]`);
  if (labels.length === 0) return "";
  const first = labels[0];
  return first ? (first.textContent ?? "").trim() : "";
}
function accessibleName(doc, el) {
  const lblBy = el.getAttribute("aria-labelledby");
  if (lblBy) {
    const resolved = resolveLabelledBy(doc, lblBy);
    if (resolved) return resolved;
  }
  const ariaLabel = el.getAttribute("aria-label");
  if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();
  const id = el.getAttribute("id");
  if (id) {
    const lbl = resolveLabelFor(doc, id);
    if (lbl) return lbl;
  }
  const tag = el.tagName.toLowerCase();
  if (tag === "img" || tag === "area") {
    const alt = el.getAttribute("alt");
    if (alt !== null) return alt.trim();
  }
  const title = el.getAttribute("title");
  if (title && title.trim()) return title.trim();
  const tc = el.textContent ?? "";
  return tc.trim();
}

// src/checks.ts
var INTERACTIVE_TAGS = /* @__PURE__ */ new Set(["a", "button", "input", "textarea", "select"]);
function elementsBySelector(doc, selector) {
  const list = doc.querySelectorAll(selector);
  const out = [];
  for (let i = 0; i < list.length; i++) {
    const v = list[i];
    if (v) out.push(v);
  }
  return out;
}
function selectorFor(el) {
  const id = el.getAttribute("id");
  if (id) return `#${id}`;
  const role = el.getAttribute("role");
  const tag = el.tagName.toLowerCase();
  return role ? `${tag}[role="${role}"]` : tag;
}
function snippetOf(el) {
  return el.outerHTML ?? `<${el.tagName.toLowerCase()}>`;
}
function checkNamePresence(doc) {
  const findings = [];
  const interactive = elementsBySelector(doc, "a, button, input, textarea, select, img, [role]");
  for (const el of interactive) {
    const tag = el.tagName.toLowerCase();
    if (tag === "input" && el.getAttribute("type") === "hidden") continue;
    if (el.getAttribute("aria-hidden") === "true") continue;
    const role = el.getAttribute("role");
    if (role === "presentation" || role === "none") continue;
    if ((tag === "img" || tag === "area") && el.getAttribute("alt") === "") continue;
    const name = accessibleName(doc, el);
    if (name) continue;
    findings.push({
      id: "R8-S1",
      impact: "serious",
      description: "Interactive or structural element has no accessible name",
      help: "Add aria-label, aria-labelledby, alt, or visible text content",
      helpUrl: "https://norkron.dev/rules/srequivz/R8-S1",
      tags: ["screen-reader", "accessible-name", "wcag-4.1.2"],
      nodes: [
        {
          target: selectorFor(el),
          html: snippetOf(el),
          failureSummary: `<${tag}> has no accessible name resolvable via aria-labelledby/aria-label/label/alt/title/text`
        }
      ]
    });
  }
  return findings;
}
var REDUNDANT_PREFIXES = [
  "image of ",
  "picture of ",
  "photo of ",
  "graphic of ",
  "icon of ",
  "image:",
  "photo:"
];
function checkNameRedundancy(doc) {
  const findings = [];
  const targets = elementsBySelector(doc, 'img, [role="img"], [aria-label]');
  for (const el of targets) {
    const name = accessibleName(doc, el).toLowerCase();
    if (!name) continue;
    for (const bad of REDUNDANT_PREFIXES) {
      if (name.startsWith(bad)) {
        findings.push({
          id: "R8-S2",
          impact: "minor",
          description: "Accessible name redundantly describes the element type",
          help: 'Remove redundant prefixes like "image of" \u2014 screen readers announce role automatically',
          helpUrl: "https://norkron.dev/rules/srequivz/R8-S2",
          tags: ["screen-reader", "accessible-name", "redundancy"],
          nodes: [
            {
              target: selectorFor(el),
              html: snippetOf(el),
              failureSummary: `Accessible name "${name}" starts with redundant prefix "${bad.trim()}"`
            }
          ]
        });
        break;
      }
    }
  }
  return findings;
}
function checkReadingOrder(doc) {
  const findings = [];
  const withFlowTo = elementsBySelector(doc, "[aria-flowto]");
  if (withFlowTo.length === 0) return findings;
  const visited = /* @__PURE__ */ new Set();
  function key(el) {
    return el.getAttribute("id") ?? `${el.tagName}@${el.outerHTML?.slice(0, 32) ?? ""}`;
  }
  const first = withFlowTo[0];
  if (!first) return findings;
  let cur = first;
  while (cur) {
    const k = key(cur);
    if (visited.has(k)) break;
    visited.add(k);
    const next = cur.getAttribute("aria-flowto");
    if (!next) break;
    const matches = doc.querySelectorAll(`[id="${next.replace(/"/g, '\\"')}"]`);
    cur = matches.length > 0 ? matches[0] ?? null : null;
  }
  const interactive = elementsBySelector(doc, INTERACTIVE_TAGS_SELECTOR);
  for (const el of interactive) {
    if (visited.has(key(el))) continue;
    findings.push({
      id: "R8-S3",
      impact: "moderate",
      description: "Reading order skips an interactive element",
      help: "Either remove aria-flowto from siblings or extend the chain to include every interactive element",
      helpUrl: "https://norkron.dev/rules/srequivz/R8-S3",
      tags: ["screen-reader", "reading-order"],
      nodes: [
        {
          target: selectorFor(el),
          html: snippetOf(el),
          failureSummary: "Element not reachable via the established aria-flowto chain"
        }
      ]
    });
  }
  return findings;
}
var INTERACTIVE_TAGS_SELECTOR = Array.from(INTERACTIVE_TAGS).join(", ");

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var AuditOptionsSchema = z.object({
  skipReadingOrder: z.boolean().optional(),
  skipNamePresence: z.boolean().optional(),
  skipNameRedundancy: z.boolean().optional()
}).strict();
var AuditInputSchema = z.object({
  options: AuditOptionsSchema.optional()
}).strict();

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: SREQUIVZ_LIB,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/audit.ts
function resolveDocument(page) {
  if (page && typeof page === "object" && "document" in page) {
    return page.document;
  }
  return page;
}
async function audit(page, options) {
  if (options !== void 0) {
    validateInput(AuditOptionsSchema, "audit", options);
  }
  const opts = options ?? {};
  const doc = resolveDocument(page);
  const findings = [];
  if (!opts.skipNamePresence) findings.push(...checkNamePresence(doc));
  if (!opts.skipNameRedundancy) findings.push(...checkNameRedundancy(doc));
  if (!opts.skipReadingOrder) findings.push(...checkReadingOrder(doc));
  return { findings };
}
function countById(result, id) {
  let n = 0;
  for (const f of result.findings) if (f.id === id) n++;
  return n;
}

// src/index.ts
var SREQUIVZ_VERSION = "0.1.2";

export { AuditInputSchema, AuditOptionsSchema, SREQUIVZ_VERSION, accessibleName, audit, checkNamePresence, checkNameRedundancy, checkReadingOrder, countById, isNamingTarget };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map